import axios from "axios";
import {
  getAccessToken,
  getUser,
  getCurrentSubscriptionId,
  getIDToken,
  getRefreshToken,
  getTokenForSubscriptionSSO
} from "services/auth";
import { endSession, SessionEndDetailCode } from "services/session-manager";
import { isSessionTerminating } from "services/session-state";

/**
 * How close to expiry (ms) a token may get before we proactively renew it on
 * the next request. Renewing — rather than ejecting — at this point is what
 * lets a backgrounded/slept tab recover instead of being logged out.
 */
const REFRESH_THRESHOLD_MS = 60_000;

/**
 * The single in-flight refresh shared across every caller (REST clients,
 * Apollo, the proactive timer). Guarantees one token exchange per renewal —
 * concurrent requests await the same promise instead of stampeding the server.
 */
let inflightRefresh: Promise<string | null> | null = null;

/**
 * Serializes every /sso/exchange-token call — subscription refresh AND
 * project exchange. Concurrent exchanges carry the same refresh_token, and
 * the backend rejects the loser with a 400, which reads as a definitive
 * rejection. One chain guarantees the exchanges never overlap.
 */
let exchangeChain: Promise<unknown> = Promise.resolve();
export function serializeTokenExchange<T>(
  exchange: () => Promise<T>
): Promise<T> {
  const next = exchangeChain.then(exchange, exchange);
  exchangeChain = next.then(
    () => undefined,
    () => undefined
  );
  return next;
}

export async function ensureValidAccessToken(): Promise<string | null> {
  const token = getAccessToken();
  if (!token) {
    return null;
  }

  const user = getUser();
  const expMs = user?.exp ? user.exp * 1000 : null;
  if (expMs !== null && expMs - Date.now() > REFRESH_THRESHOLD_MS) {
    return token;
  }

  if (!inflightRefresh) {
    inflightRefresh = refreshAccessToken().finally(() => {
      inflightRefresh = null;
    });
  }
  return inflightRefresh;
}

const TOKEN_EXCHANGE_PATH = "/sso/exchange-token";

function isTokenExchangeRequest(url?: string): boolean {
  return typeof url === "string" && url.includes(TOKEN_EXCHANGE_PATH);
}

/**
 * Shared axios request interceptor — the one place every REST client attaches
 * its bearer token. Awaits the coordinator so a near-expiry token is renewed
 * (not ejected) before the request goes out.
 */
export async function attachFreshToken(config: any): Promise<any> {
  // The refresh endpoint authenticates via the request body (refresh_token +
  // id_token) and needs no access token. It must never re-enter the
  // coordinator — that would await the very refresh already in flight (deadlock).
  if (isTokenExchangeRequest(config?.url)) {
    return config;
  }

  // A request that already carries its own bearer authenticates itself — e.g.
  // the public lead-time pages, which run logged-out with a feature-workflow
  // token from the URL and have no session accToken to validate. It isn't part
  // of the app session, so neither require the session token nor cancel it on
  // teardown; leaving its header untouched keeps these public flows working.
  if (config?.headers?.Authorization) {
    return config;
  }

  // Session is being torn down (logout / unrecoverable refresh failure): cancel
  // rather than fire a doomed request that 401s and spams Sentry on the way out.
  if (isSessionTerminating()) {
    throw new axios.CanceledError(
      "auth: session terminating — request canceled"
    );
  }

  const token = await ensureValidAccessToken();
  if (!token) {
    throw new axios.CanceledError("auth: no valid token — request canceled");
  }
  if (config?.headers && !config.headers.Authorization) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
}

/**
 * Single auth-header source for Apollo's authLink and the graphql-ws
 * connectionParams. Resolving this per request/connection (rather than baking
 * the token in at client creation) is what keeps a long-lived Apollo client and
 * its socket using a current token instead of a stale one.
 */
export async function authHeader(): Promise<{ Authorization: string }> {
  // Mirror attachFreshToken: with no session (teardown or cleared tokens) a
  // blank Bearer would still reach Hasura and bounce as "Malformed
  // Authorization header" noise — cancel locally instead.
  if (isSessionTerminating()) {
    throw new axios.CanceledError(
      "auth: session terminating — request canceled"
    );
  }
  const token = await ensureValidAccessToken();
  if (!token) {
    throw new axios.CanceledError("auth: no valid token — request canceled");
  }
  return { Authorization: `Bearer ${token}` };
}

/**
 * Pre-warm the token when the tab is reactivated. While a tab is backgrounded
 * or the machine is asleep, timers are throttled/paused — so the proactive
 * refresh may not have fired. Refreshing on wake (before the first request)
 * recovers the session and lets the WebSocket reconnect with a current token.
 * Returns a disposer that removes the listeners.
 */
export function installTokenRefreshOnWake(): () => void {
  const onWake = () => {
    if (document.visibilityState !== "visible") {
      return;
    }
    void ensureValidAccessToken();
  };
  document.addEventListener("visibilitychange", onWake);
  window.addEventListener("focus", onWake);
  return () => {
    document.removeEventListener("visibilitychange", onWake);
    window.removeEventListener("focus", onWake);
  };
}

/**
 * Backoff between refresh attempts. The refresh token lives ~24h and the
 * exchange authenticates with it (not the expiring access token), so a
 * transient blip at the renewal moment must not end the session — a retry
 * seconds later still succeeds, even past the access token's expiry.
 */
const REFRESH_RETRY_DELAYS_MS = [1_000, 2_000];

/**
 * How long to wait for connectivity before declaring the refresh failed. A
 * laptop waking from sleep typically needs 5-15s to re-associate with Wi-Fi —
 * far longer than the retry backoff — so firing (or failing) exchanges during
 * that window would end sessions the network was about to save.
 */
const OFFLINE_GRACE_MS = 30_000;

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

function isBrowserOffline(): boolean {
  return typeof navigator !== "undefined" && navigator.onLine === false;
}

/**
 * Resolves true as soon as the browser reports connectivity (or immediately
 * if it never lost it), false when the grace window closes while still
 * offline. navigator.onLine === false is trustworthy: it means requests are
 * guaranteed to fail, so there is no point attempting one.
 */
function waitForOnline(graceMs: number): Promise<boolean> {
  if (!isBrowserOffline()) {
    return Promise.resolve(true);
  }
  return new Promise((resolve) => {
    const settle = (online: boolean) => {
      window.removeEventListener("online", onOnline);
      clearTimeout(timer);
      resolve(online);
    };
    const onOnline = () => settle(true);
    const timer = setTimeout(() => settle(!isBrowserOffline()), graceMs);
    window.addEventListener("online", onOnline);
  });
}

/**
 * The backend examined the refresh/id token and said no — retrying cannot
 * change that verdict, it only delays the inevitable logout.
 */
function isDefinitiveRejection(error: any): boolean {
  const status = error?.response?.status;
  return status === 400 || status === 401 || status === 403;
}

/**
 * Wait out a dead interface before spending a retry attempt. Returns false
 * when the caller must stop: the session ended, or connectivity never came
 * back inside the grace window.
 */
async function awaitOnlineForAttempt(): Promise<boolean> {
  const backOnline = await waitForOnline(OFFLINE_GRACE_MS);
  if (isSessionTerminating()) {
    return false;
  }
  if (!backOnline) {
    void endSession(
      "refresh_failed",
      "browser offline — connectivity did not return within grace window",
      "offline_grace_expired"
    );
    return false;
  }
  return true;
}

/** Back off, then decide whether this attempt is still worth making. */
async function prepareAttempt(attempt: number): Promise<boolean> {
  if (attempt > 0) {
    await sleep(REFRESH_RETRY_DELAYS_MS[attempt - 1]);
  }
  if (isSessionTerminating()) {
    return false;
  }
  if (isBrowserOffline()) {
    // Don't spend an attempt on a dead interface — wait for the browser's
    // online signal (wake-before-Wi-Fi), then exchange on a live network.
    return awaitOnlineForAttempt();
  }
  return true;
}

async function attemptTokenExchange(
  idToken: string,
  refreshToken: string
): Promise<{ token: string | null; error: unknown }> {
  try {
    // CIQClient never throws — failures come back as { success, error }.
    const response: any = await serializeTokenExchange(() =>
      getTokenForSubscriptionSSO({
        subscriptionId: getCurrentSubscriptionId(),
        idToken,
        refreshToken
      })
    );
    const newToken = response?.data?.tokens?.AccessToken;
    if (newToken) {
      return { token: newToken, error: null };
    }
    return {
      token: null,
      error:
        response?.error ?? new Error("Token refresh returned no access token")
    };
  } catch (err) {
    return { token: null, error: err };
  }
}

function rejectionDetailCode(error: unknown): SessionEndDetailCode {
  const status = (error as any)?.response?.status;
  return status === 400 || status === 401 || status === 403
    ? (`rejected_${status}` as SessionEndDetailCode)
    : "exhausted_transient";
}

async function refreshAccessToken(): Promise<string | null> {
  // Without these the exchange is doomed — the backend rejects it outright
  // ("refresh_token is required"). Firing it anyway (e.g. a refresh racing a
  // logout that just cleared storage) only produces backend error noise and
  // futile retries; end the session locally instead.
  const refreshToken = getRefreshToken();
  const idToken = getIDToken();
  if (!refreshToken || !idToken) {
    void endSession(
      "refresh_failed",
      "refresh credentials missing from storage",
      "missing_credentials"
    );
    return null;
  }

  let lastError: unknown = null;
  for (
    let attempt = 0;
    attempt <= REFRESH_RETRY_DELAYS_MS.length;
    attempt += 1
  ) {
    const canAttempt = await prepareAttempt(attempt);
    if (!canAttempt) {
      return null;
    }

    const { token, error } = await attemptTokenExchange(idToken, refreshToken);
    if (token) {
      return token;
    }
    lastError = error;

    if (isDefinitiveRejection(lastError)) {
      break;
    }
  }
  void endSession("refresh_failed", lastError, rejectionDetailCode(lastError));
  return null;
}
